import { AxisBottom } from "@visx/axis";
import { Group } from "@visx/group";
import { scaleBand, scaleLinear } from "@visx/scale";
import { Bar } from "@visx/shape";
import lightFormat from "date-fns/lightFormat";

type TNode = {
  date: Date;
  value: number;
};

type TopLeftBottomRight = {
  top: number;
  left: number;
  bottom: number;
  right: number;
};

interface IBarChartProps {
  ariaLabel?: string;
  chart: {
    margin: TopLeftBottomRight;
    padding: TopLeftBottomRight;
    width: number;
    height: number;
    primaryColor: string;
    secondaryColor?: string;
  };
  nodes: TNode[];
}

const x = (node: TNode) => node.date;
const y = (node: TNode) => node.value;

export default function BarChart({
  ariaLabel,
  chart: { margin, padding, primaryColor, width, height },
  nodes,
}: IBarChartProps) {
  const yMax = height - margin.top - margin.bottom;
  const xMax = width - margin.left - margin.right;

  const xScale = scaleBand({
    range: [padding.left, xMax - padding.right],
    domain: nodes.map(x),
    paddingInner: 0.4,
  });

  const xScaleValues = nodes.map(x);
  const yScaleValues = nodes.map(y);

  const yScale = scaleLinear({
    range: [padding.top, yMax - padding.bottom],
    domain: [Math.max(...yScaleValues), 0],
  });

  return (
    <div className="flex" aria-label={ariaLabel}>
      <svg width={width} height={height}>
        <Group top={margin.top} left={margin.left}>
          {nodes.map((node, index) => {
            const barWidth = xScale.bandwidth();
            const barHeight = yMax - yScale(y(node));
            const barX = xScale(x(node));
            const barY = yMax - barHeight;
            const key = `BarChart/Bar/${index}`;

            return <Bar key={key} x={barX} y={barY} rx={2} width={barWidth} height={barHeight} fill={primaryColor} />;
          })}

          <AxisBottom
            top={yMax}
            numTicks={6}
            tickValues={xScaleValues}
            tickFormat={(tickValue) => {
              if (tickValue instanceof Date) {
                return `'${lightFormat(tickValue, "yy")}`;
              }

              return "";
            }}
            scale={xScale}
            hideTicks={false}
            hideAxisLine={false}
            stroke="rgba(0, 0, 0, 0.06)"
            tickStroke="rgba(0, 0, 0, 0.06)"
            tickLabelProps={() => ({
              fontWeight: 500,
              fontSize: 11,
              lineHeight: 1.33,
              letterSpacing: 0.6,
              fill: "#627d98",
              textAnchor: "middle",
            })}
          />
        </Group>
      </svg>
    </div>
  );
}
